import { Badge, Center, Group, Loader, Table, Text } from '@mantine/core';
import { useSubscription } from 'urql';
import { CallErrorBadge } from './call-error-badge';
import { RecentCallsSubscription } from './call-graphql';
import { callStatusColor, formatDateTime, formatDuration, formatPhone } from './format';
import { useCallErrorSummary } from './use-call-error-summary';

export function RecentCallsTable({
  organizationId,
  limit,
  onSelect,
}: {
  organizationId: string;
  limit: number;
  onSelect?: (callControlId: string) => void;
}) {
  const [{ data }] = useSubscription({ query: RecentCallsSubscription, variables: { organizationId, limit } });
  const errorSummary = useCallErrorSummary(
    organizationId,
    (data?.callLogs ?? []).map((c) => c.callControlId),
  );

  if (!data) {
    return (
      <Center py="md">
        <Loader size="sm" />
      </Center>
    );
  }

  if (data.callLogs.length === 0) {
    return (
      <Text c="dimmed" size="sm" ta="center" py="md">
        No calls yet
      </Text>
    );
  }

  return (
    <Table striped highlightOnHover>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>Time</Table.Th>
          <Table.Th>From</Table.Th>
          <Table.Th>To</Table.Th>
          <Table.Th>Flow</Table.Th>
          <Table.Th>Duration</Table.Th>
          <Table.Th>Status</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {data.callLogs.map((call) => (
          <Table.Tr
            key={call.id}
            style={onSelect ? { cursor: 'pointer' } : undefined}
            onClick={onSelect ? () => onSelect(call.callControlId) : undefined}
          >
            <Table.Td>
              <Text size="sm">{formatDateTime(call.startedAt)}</Text>
            </Table.Td>
            <Table.Td>
              <Text size="sm">{formatPhone(call.fromNumber)}</Text>
            </Table.Td>
            <Table.Td>
              <Text size="sm">{formatPhone(call.toNumber)}</Text>
            </Table.Td>
            <Table.Td>
              <Text size="sm" c={call.flow ? undefined : 'dimmed'}>
                {call.flow?.name ?? 'Unknown'}
              </Text>
            </Table.Td>
            <Table.Td>
              <Text size="sm">{formatDuration(call.durationSeconds)}</Text>
            </Table.Td>
            <Table.Td>
              <Group gap={6} wrap="nowrap">
                <Badge variant="light" color={callStatusColor(call.status)} size="sm">
                  {call.status}
                </Badge>
                <CallErrorBadge summary={errorSummary.get(call.callControlId)} />
              </Group>
            </Table.Td>
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  );
}
